<script setup>
/* eslint-disable @intlify/vue-i18n/no-raw-text, vue/no-bare-strings-in-template */
// Formulário único de campanha, em PASSOS (decisão 30, que revoga a página única
// da decisão 28). Um bloco por tela, barra de passos no topo, Continuar e Voltar,
// e a última tela é a de conferir. Vale para WhatsApp por QR code
// (Channel::Api/WAHA), WhatsApp oficial e SMS shortcode.
//
// Os blocos são os MESMOS da página única: isto é mudança de navegação, não
// reescrita de conteúdo. Eles ficam montados (v-show, nunca v-if) porque o
// MessageBlock guarda o parser do modelo aprovado, de que o payload e a
// validação dependem, e porque Voltar tem de achar tudo como estava.
//
// A regra mora em campaignFormHelpers.js e cada bloco é um componente: foi
// exatamente a mistura de regra com template que fez o formulário anterior
// chegar a 2.523 linhas.
import { computed, onMounted, reactive, ref, watch } from 'vue';
import { useMapGetter, useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import FunnelAPI from 'dashboard/api/funnel';
import CampaignsAPI from 'dashboard/api/campaigns';
import CaptainAssistantAPI from 'dashboard/api/captain/assistant';
import { uploadFile } from 'dashboard/helper/uploadHelper';
import {
  takeKanbanCampaignAudience,
  clearKanbanCampaignAudience,
} from 'dashboard/helper/kanbanCampaignHandoff';

import Button from 'dashboard/components-next/button/Button.vue';
import FormBlock from './FormBlock.vue';
import CampaignStepBar from './CampaignStepBar.vue';
import ChannelBlock from './ChannelBlock.vue';
import AudienceBlock from './AudienceBlock.vue';
import MessageBlock from './MessageBlock.vue';
import ScheduleBlock from './ScheduleBlock.vue';
import CampaignSummary from './CampaignSummary.vue';
import {
  blockingIssueForStep,
  buildCampaignPayload,
  buildAudience,
  createInitialState,
  estimateDuration,
  firstBlockingIssue,
  parseCsvContacts,
  resolveMessageForRecipient,
  visibleSteps,
} from './campaignFormHelpers';
import {
  CAMPAIGN_CHANNELS,
  CHANNEL_LABELS,
  channelAvailability,
  isInboxUsableForCampaign,
  resolveChannelEntry,
} from './campaignChannelHelpers';

const props = defineProps({
  // Canal inicial: a página de SMS abre já em SMS, a de WhatsApp em QR code.
  initialChannel: { type: String, default: 'waha' },
  // Família de canais quando a pessoa já escolheu o canal fora, no cartão "Nova
  // ação de marketing": ['waha','official'] pela rota de WhatsApp, ['sms'] pela
  // de SMS. Vazio = entrou por "Nova campanha" dentro de Campanhas, sem canal
  // definido, e o passo 1 aparece inteiro.
  channelFamily: { type: Array, default: () => [] },
  submitting: { type: Boolean, default: false },
});

const emit = defineEmits(['submit', 'saveDraft', 'cancel']);

const store = useStore();

const MISSING_LABELS = {
  TITLE: 'dar um nome à campanha',
  INBOX: 'escolher a caixa que envia',
  KANBAN_ITEMS: 'trazer os cartões marcados do Kanban',
  AUDIENCE_LABELS: 'escolher as etiquetas do público',
  AUDIENCE_STAGE: 'escolher o funil e a etapa',
  AUDIENCE_FUNNEL: 'escolher o funil',
  AUDIENCE_AGENT: 'escolher o atendente',
  AUDIENCE_ATTRIBUTE: 'escolher o campo do CRM',
  AUDIENCE_CSV: 'enviar a planilha com os contatos',
  MESSAGE: 'escrever a mensagem',
  TEMPLATE: 'escolher o modelo aprovado',
  TEMPLATE_PARAMS: 'preencher os campos do modelo',
  SCHEDULED_AT: 'dizer a data e a hora do agendamento',
  KA_INBOX: 'escolher a caixa de WhatsApp da palavra-chave',
  KA_MESSAGE: 'escrever a mensagem da palavra-chave',
  KA_TEMPLATE: 'escolher o modelo da palavra-chave',
};

const VARIABLES = ['primeiro_nome', 'nome', 'telefone'];

const state = reactive(createInitialState());
state.channelType = props.initialChannel;

const messageBlockRef = ref(null);
const scheduleBlockRef = ref(null);

const getters = {
  allInboxes: useMapGetter('inboxes/getInboxes'),
  labels: useMapGetter('labels/getLabels'),
  whatsappInboxes: useMapGetter('inboxes/getWhatsappCampaignInboxes'),
  templatesFor: useMapGetter('inboxes/getFilteredWhatsAppTemplates'),
  agents: useMapGetter('agents/getAgents'),
  teams: useMapGetter('teams/getTeams'),
  contactAttributes: useMapGetter('attributes/getContactAttributes'),
};

const aiAssistants = ref([]);
const funnels = ref([]);
const loadingFunnels = ref(false);
const kanbanFunnelName = ref('');

const csvContacts = ref([]);
const csvFileName = ref('');
const csvInvalidCount = ref(0);

const suppressionLoading = ref(false);
const suppressionError = ref('');
const suppressionResult = ref(null);
const suppressionRetryFile = ref(null);

const preview = ref(null);
const previewLoading = ref(false);
const previewError = ref('');

const fetchFunnels = async () => {
  if (funnels.value.length || loadingFunnels.value) return;
  loadingFunnels.value = true;
  try {
    const { data } = await FunnelAPI.get();
    funnels.value = data || [];
  } catch (error) {
    funnels.value = [];
  } finally {
    loadingFunnels.value = false;
  }
};

const clearKanbanItems = () => {
  state.kanbanItemIds = [];
  clearKanbanCampaignAudience();
};

onMounted(async () => {
  // Cartões marcados no Kanban: chegam por sessionStorage, nunca pela URL. É a
  // PRIMEIRA coisa do onMounted, antes de qualquer await, para o público já estar
  // preenchido no primeiro render.
  const handoff = takeKanbanCampaignAudience();
  if (handoff) {
    state.audienceType = 'kanban_items';
    state.kanbanItemIds = handoff.itemIds;
    kanbanFunnelName.value = handoff.funnelName;
  }

  store.dispatch('agents/get');
  store.dispatch('teams/get');
  store.dispatch('attributes/get');
  fetchFunnels();
  try {
    const { data } = await CaptainAssistantAPI.get();
    aiAssistants.value = data?.payload || data || [];
  } catch (error) {
    aiAssistants.value = [];
  }
});

// ===== Canal e caixa =====
// Caixa desconectada não entra na lista (decisão 30). O critério é o estado real
// da conexão, calculado em campaignChannelHelpers a partir de
// `inboxConnectionStatus`, e NÃO o nome da caixa.
const availability = computed(() =>
  channelAvailability(getters.allInboxes.value || [], CAMPAIGN_CHANNELS)
);

const inboxesForChannel = computed(
  () => availability.value[state.channelType]?.inboxes || []
);

const inboxOptions = computed(() =>
  inboxesForChannel.value.map(inbox => ({ value: inbox.id, label: inbox.name }))
);

const selectedInbox = computed(() =>
  inboxesForChannel.value.find(inbox => inbox.id === state.inboxId)
);

const channelMode = computed(() => state.channelType);
const isFreeText = computed(() => channelMode.value !== 'official');
const isWaha = computed(() => channelMode.value === 'waha');

// Enquanto a pessoa não escolher o canal, o formulário pode corrigir o canal
// inicial; depois de ela escolher, não mexe mais.
const channelTouched = ref(false);

const changeChannel = value => {
  channelTouched.value = true;
  if (state.channelType === value) return;
  // Canal sem caixa utilizável não é escolhível: o cartão já vem desabilitado,
  // e aqui fica a mesma guarda para quem chegar por outro caminho.
  if (availability.value[value]?.available === false) return;
  state.channelType = value;
  state.inboxId = null;
  state.templateId = null;
  state.templateVariants = [];
  state.attachedMedia = null;
  (state.messageVariants || []).forEach(variant => {
    variant.media = null;
  });
};

// Canal inicial indisponível e um único canal utilizável na conta: o formulário
// abre nesse canal. Não é "escolher sozinho uma escolha ruim" (decisão 30): é a
// única opção que existe, e deixar a tela num canal morto só faz a pessoa
// adivinhar. Com dois ou mais utilizáveis, ela escolhe.
watch(
  availability,
  value => {
    if (channelTouched.value) return;
    if (value[state.channelType]?.available) return;
    const usable = CAMPAIGN_CHANNELS.filter(
      channel => value[channel]?.available
    );
    if (usable.length === 1) state.channelType = usable[0];
  },
  { immediate: true }
);

// Caixa única: evita um seletor com uma opção só.
watch(
  inboxOptions,
  options => {
    if (!state.inboxId && options.length === 1)
      state.inboxId = options[0].value;
  },
  { immediate: true }
);

watch(
  () => state.inboxId,
  () => {
    state.templateId = null;
    state.templateVariants = [];
  }
);

// ===== Opções =====
const labelOptions = computed(() =>
  (getters.labels.value || []).map(label => ({
    value: label.id,
    label: label.title,
  }))
);
const agentOptions = computed(() =>
  (getters.agents.value || []).map(agent => ({
    value: agent.id,
    label: agent.name,
  }))
);
const teamOptions = computed(() =>
  (getters.teams.value || []).map(team => ({
    value: team.id,
    label: team.name,
  }))
);
const aiAssistantOptions = computed(() =>
  aiAssistants.value.map(assistant => ({
    value: assistant.id,
    label: assistant.name,
  }))
);
const funnelOptions = computed(() =>
  funnels.value.map(funnel => ({ value: funnel.id, label: funnel.name }))
);
const stagesOf = funnelId => {
  const funnel = funnels.value.find(item => item.id === funnelId);
  if (!funnel || !funnel.stages) return [];
  return Object.entries(funnel.stages)
    .map(([key, stage]) => ({
      value: key,
      label: stage.name || key,
      position: Number(stage.position) || 0,
    }))
    .sort((a, b) => a.position - b.position);
};
const stageOptions = computed(() => stagesOf(state.selectedFunnel));
const kanbanDestStageOptions = computed(() =>
  stagesOf(state.kanbanDestFunnelId)
);

const contactAttributeOptions = computed(() =>
  (getters.contactAttributes.value || []).map(attribute => ({
    value: attribute.attributeKey,
    label: attribute.attributeDisplayName || attribute.attributeKey,
  }))
);
const selectedContactAttribute = computed(() =>
  (getters.contactAttributes.value || []).find(
    attribute => attribute.attributeKey === state.contactAttrKey
  )
);
const contactAttrValueOptions = computed(() => {
  const attribute = selectedContactAttribute.value;
  if (!attribute || attribute.attributeDisplayType !== 'list') return [];
  return (attribute.attributeValues || []).map(value => ({
    value,
    label: value,
  }));
});

const templateOptions = computed(() => {
  if (!state.inboxId || isFreeText.value) return [];
  const templates = getters.templatesFor.value(state.inboxId) || [];
  return templates.map(template => ({
    value: template.id,
    label: `${template.name.replace(/_/g, ' ')} (${template.language || 'en'})`,
    template,
  }));
});
const selectedTemplate = computed(
  () =>
    templateOptions.value.find(o => o.value === state.templateId)?.template ||
    null
);

// A caixa de destino da palavra-chave também envia mensagem, então segue a
// mesma regra: desconectada não aparece.
const usableWhatsappInboxes = computed(() =>
  (getters.whatsappInboxes.value || []).filter(isInboxUsableForCampaign)
);
const whatsappInboxOptions = computed(() =>
  usableWhatsappInboxes.value.map(inbox => ({
    value: inbox.id,
    label: inbox.name,
  }))
);
const kaTargetInbox = computed(() =>
  usableWhatsappInboxes.value.find(inbox => inbox.id === state.kaTargetInboxId)
);
const kaTargetIsFreeText = computed(
  () => kaTargetInbox.value?.channel_type === 'Channel::Api'
);
const kaTemplateOptions = computed(() => {
  if (!state.kaTargetInboxId) return [];
  return (getters.templatesFor.value(state.kaTargetInboxId) || []).map(
    template => ({
      value: template.id,
      label: `${template.name.replace(/_/g, ' ')} (${template.language || 'en'})`,
      template,
    })
  );
});
const kaSelectedTemplate = computed(
  () =>
    kaTemplateOptions.value.find(o => o.value === state.kaTemplateId)
      ?.template || null
);

// Modelos da etapa do funil (os mesmos que alimentam a Mensagem Rápida).
const stageTemplates = computed(() => {
  const funnel = funnels.value.find(item => item.id === state.selectedFunnel);
  const stage = funnel?.stages?.[state.selectedStage];
  return stage?.message_templates || [];
});

// ===== Público =====
const changeAudienceType = type => {
  state.audienceType = type;
  state.selectedAudience = [];
  state.selectedFunnel = null;
  state.selectedStage = null;
  if (type !== 'kanban_items') {
    state.kanbanItemIds = [];
    clearKanbanCampaignAudience();
  }
  csvContacts.value = [];
  csvFileName.value = '';
  csvInvalidCount.value = 0;
};

watch(
  () => state.selectedFunnel,
  () => {
    state.selectedStage = null;
  }
);
watch(
  () => state.kanbanDestFunnelId,
  () => {
    state.kanbanDestStageId = null;
  }
);
watch(
  () => state.contactAttrKey,
  () => {
    state.contactAttrValue = '';
  }
);

const csvChipText = computed(() => {
  const base = `${csvFileName.value} — ${csvContacts.value.length} válidos`;
  return csvInvalidCount.value
    ? `${base} · ${csvInvalidCount.value} ignorados`
    : base;
});

const handleCsvUpload = event => {
  const file = event.target.files?.[0];
  if (!file) return;
  if (/\.(xlsx|xls)$/i.test(file.name)) {
    useAlert(
      'Arquivo do Excel (.xlsx/.xls) não é lido aqui. Salve a planilha como CSV (UTF-8) e envie de novo.'
    );
    event.target.value = '';
    return;
  }
  csvFileName.value = file.name;
  const reader = new FileReader();
  reader.onload = readEvent => {
    const { contacts, invalid } = parseCsvContacts(readEvent.target.result);
    csvContacts.value = contacts;
    csvInvalidCount.value = invalid.length;
  };
  reader.readAsText(file);
};

const removeCsv = () => {
  csvContacts.value = [];
  csvFileName.value = '';
  csvInvalidCount.value = 0;
};

const downloadCsvTemplate = () => {
  const content = [
    'TELEFONE;NOME',
    '+5511999999999;Maria Silva',
    '+5521988887777;João Santos',
    '11966665555;Ana Costa',
  ].join('\n');
  const blob = new Blob([`﻿${content}`], {
    type: 'text/csv;charset=utf-8;',
  });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = 'modelo_campanha.csv';
  link.click();
  URL.revokeObjectURL(url);
};

const processSuppressionFile = async file => {
  if (!file || suppressionLoading.value) return;
  suppressionLoading.value = true;
  suppressionError.value = '';
  suppressionResult.value = null;
  suppressionRetryFile.value = file;
  try {
    const contents = await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = event => resolve(event.target.result);
      reader.onerror = () => reject(new Error('read_error'));
      reader.readAsText(file);
    });
    const phones = contents
      .split('\n')
      .map(line => line.split(/[,;\t]/)[0]?.trim())
      .filter(Boolean);
    if (!phones.length) throw new Error('empty_file');
    suppressionResult.value = await store.dispatch(
      'campaigns/suppressPhones',
      phones
    );
  } catch (error) {
    suppressionError.value =
      error.message === 'empty_file'
        ? 'A planilha não tem nenhum telefone.'
        : 'Não foi possível enviar a lista de descadastro.';
  } finally {
    suppressionLoading.value = false;
  }
};

const handleSuppressionUpload = event => {
  const file = event.target.files?.[0];
  event.target.value = '';
  processSuppressionFile(file);
};

// ===== Prévia do público (contagem real) =====
let previewToken = 0;
const audiencePayload = computed(() =>
  buildAudience(state, { csvContacts: csvContacts.value })
);

const loadPreview = async () => {
  const audience = audiencePayload.value;
  if (!audience.length) {
    preview.value = null;
    previewError.value = '';
    return;
  }
  const token = (previewToken += 1);
  previewLoading.value = true;
  previewError.value = '';
  try {
    const { data } = await CampaignsAPI.audiencePreview({
      audience,
      inbox_id: state.inboxId,
      drip_config: { channel_mode: channelMode.value },
    });
    if (token !== previewToken) return;
    preview.value = data;
  } catch (error) {
    if (token !== previewToken) return;
    previewError.value = 'Não foi possível conferir o público.';
    preview.value = null;
  } finally {
    if (token === previewToken) previewLoading.value = false;
  }
};

let previewTimer = null;
watch(
  () => [
    JSON.stringify(audiencePayload.value),
    channelMode.value,
    state.inboxId,
  ],
  () => {
    clearTimeout(previewTimer);
    previewTimer = setTimeout(loadPreview, 400);
  },
  { immediate: true }
);

const receiving = computed(() => preview.value?.receiving || 0);
const excluded = computed(() => preview.value?.excluded || 0);
const duration = computed(() => estimateDuration(receiving.value, state.pace));

// ===== Mensagem e resumo =====
const messageSource = computed(() =>
  isFreeText.value
    ? state.wahaMessage
    : messageBlockRef.value?.templateParserRef?.renderedTemplate || ''
);

const previewMessage = computed(() =>
  resolveMessageForRecipient(
    messageSource.value,
    preview.value?.first_recipient,
    {
      nameFallback: state.nameFallback,
    }
  )
);

const previewRecipientName = computed(
  () => preview.value?.first_recipient?.name || ''
);

const whenLabel = computed(() => {
  const pace = { fast: 'rápido', normal: 'normal', safe: 'seguro' }[state.pace];
  if (state.scheduleMode === 'later' && state.scheduledAt) {
    return `${new Date(state.scheduledAt).toLocaleString('pt-BR')} · ritmo ${pace}`;
  }
  return `Agora · ritmo ${pace}`;
});

const finishLabel = computed(() => {
  if (!receiving.value) return '';
  const { minutes, days } = duration.value;
  if (days > 1) return `${days} dias`;
  if (minutes >= 60) return `${Math.round((minutes / 60) * 10) / 10} horas`;
  return `${minutes} minutos`;
});

const templateParamsMissing = computed(() => {
  if (isFreeText.value || !selectedTemplate.value) return false;
  const parser = messageBlockRef.value?.templateParserRef;
  if (!parser) return true;
  return parser.v$?.$invalid === true || parser.isFormInvalid === true;
});

const blockingIssue = computed(() =>
  firstBlockingIssue(state, {
    isFreeText: isFreeText.value,
    channelMode: channelMode.value,
    csvCount: csvContacts.value.length,
    templateParamsMissing: templateParamsMissing.value,
    kaTargetIsFreeText: kaTargetIsFreeText.value,
  })
);

const missingLabel = computed(() =>
  blockingIssue.value ? MISSING_LABELS[blockingIssue.value.reason] || '' : ''
);

const pendingBlock = computed(() => blockingIssue.value?.block || 0);

// ===== Passo a passo (decisão 30) =====
// O passo 1 some quando o canal chegou escolhido de fora E não sobrou escolha
// nenhuma para fazer nele (um canal utilizável, uma caixa utilizável).
const skipChannelStep = ref(false);
// Guarda para resolver a entrada UMA vez por família: uma resolução por
// digitação de caixa faria o passo 1 sumir e voltar debaixo da mão da pessoa.
const resolvedForFamily = ref(null);
const currentIndex = ref(0);
const furthestIndex = ref(0);

const applyChannelEntry = () => {
  const familyKey = props.channelFamily.join(',');
  if (!familyKey || resolvedForFamily.value === familyKey) return;
  // Sem caixa nenhuma carregada ainda, não dá para decidir: espera o store.
  if (!(getters.allInboxes.value || []).length) return;

  const entry = resolveChannelEntry({
    family: props.channelFamily,
    availability: availability.value,
    fallbackChannel: props.initialChannel,
  });
  resolvedForFamily.value = familyKey;
  if (entry.channel) state.channelType = entry.channel;
  if (entry.inboxId) state.inboxId = entry.inboxId;
  skipChannelStep.value = entry.skipChannelStep;
  if (entry.skipChannelStep) currentIndex.value = 0;
};

watch(
  () => [getters.allInboxes.value, props.channelFamily],
  applyChannelEntry,
  { immediate: true, deep: true }
);

const steps = computed(() =>
  visibleSteps({ skipChannelStep: skipChannelStep.value })
);

// Numero mostrado no cabecalho do bloco. Vem da posicao na barra, nao de um
// numero fixo: quando o passo 1 e pulado, a barra renumera e o bloco tinha que
// renumerar junto, senao a barra diz "1 Para quem" e o bloco diz "2 Para quem".
const numeroDoBloco = id => {
  const posicao = steps.value.findIndex(step => step.id === id);
  return posicao === -1 ? undefined : posicao + 1;
};

const currentStep = computed(
  () => steps.value[Math.min(currentIndex.value, steps.value.length - 1)]
);
const isReviewStep = computed(() => currentStep.value?.id === 'review');
const isFirstStep = computed(() => currentIndex.value === 0);

const stepIssue = computed(() =>
  blockingIssueForStep(
    state,
    {
      isFreeText: isFreeText.value,
      channelMode: channelMode.value,
      csvCount: csvContacts.value.length,
      templateParamsMissing: templateParamsMissing.value,
      kaTargetIsFreeText: kaTargetIsFreeText.value,
    },
    currentStep.value?.id
  )
);

// O botão diz o que falta quando trava, em vez de só ficar cinza.
const continueLabel = computed(() => {
  if (!stepIssue.value) return 'Continuar';
  return `Falta ${MISSING_LABELS[stepIssue.value.reason] || 'preencher este passo'}`;
});

const canContinue = computed(() => !stepIssue.value);

const goToStep = index => {
  if (index < 0 || index >= steps.value.length) return;
  // Só avança com o passo válido; voltar é sempre livre.
  if (index > currentIndex.value && !canContinue.value) return;
  currentIndex.value = index;
  furthestIndex.value = Math.max(furthestIndex.value, index);
};

const goNext = () => goToStep(currentIndex.value + 1);
const goBack = () => {
  if (currentIndex.value > 0) currentIndex.value -= 1;
};

// Barra de passos: clique só chega até onde a pessoa já esteve, e nunca para a
// frente com o passo atual travado.
const jumpToStep = index => {
  if (index <= currentIndex.value) {
    currentIndex.value = index;
    return;
  }
  goToStep(index);
};

const showStep = stepId => currentStep.value?.id === stepId;

const minDateTime = computed(() => {
  const now = new Date();
  return new Date(now.getTime() - now.getTimezoneOffset() * 60000)
    .toISOString()
    .slice(0, 16);
});

// ===== Envio =====
const buildPayload = () => {
  const parser = messageBlockRef.value?.templateParserRef;
  const template = selectedTemplate.value;
  const templateVariants = [
    state.templateId,
    ...state.templateVariants.map(variant => variant.templateId),
  ]
    .filter(Boolean)
    .filter((id, index, list) => list.indexOf(id) === index)
    .map(id => {
      const found = templateOptions.value.find(o => o.value === id)?.template;
      if (!found) return null;
      const isMain = id === state.templateId;
      return {
        name: found.name || '',
        namespace: found.namespace || '',
        category: found.category || 'UTILITY',
        language: found.language || 'en_US',
        processed_params: isMain ? parser?.processedParams || {} : {},
        body:
          (isMain ? parser?.renderedTemplate : null) ||
          (found.components || []).find(c => c.type === 'BODY')?.text ||
          '',
      };
    })
    .filter(Boolean);

  const kaParser = scheduleBlockRef.value?.kaTemplateParserRef;
  return buildCampaignPayload(state, {
    channelMode: channelMode.value,
    isFreeText: isFreeText.value,
    templateContent: parser?.renderedTemplate || '',
    templateParams: template
      ? {
          name: template.name || '',
          namespace: template.namespace || '',
          category: template.category || 'UTILITY',
          language: template.language || 'en_US',
          processed_params: parser?.processedParams || {},
        }
      : {},
    templateVariants,
    keywordRenderedMessage: kaParser?.renderedTemplate || '',
    keywordTemplateParams: kaSelectedTemplate.value
      ? {
          name: kaSelectedTemplate.value.name || '',
          namespace: kaSelectedTemplate.value.namespace || '',
          category: kaSelectedTemplate.value.category || 'UTILITY',
          language: kaSelectedTemplate.value.language || 'en_US',
          processed_params: kaParser?.processedParams || {},
        }
      : {},
    kaTargetIsFreeText: kaTargetIsFreeText.value,
    csvContacts: csvContacts.value,
  });
};

const handleSubmit = () => {
  if (missingLabel.value || receiving.value === 0) return;
  clearKanbanCampaignAudience();
  emit('submit', { payload: buildPayload(), start: true });
};

// O backend já cria a campanha como rascunho (drip_status = draft) e ela só sai
// com "Iniciar", então "Salvar rascunho" é um caminho real, não um enfeite.
const handleSaveDraft = () => {
  if (!state.title?.trim() || !state.inboxId) return;
  clearKanbanCampaignAudience();
  emit('submit', { payload: buildPayload(), start: false });
};

const insertVariable = variable => {
  state.wahaMessage = `${state.wahaMessage || ''}{${variable}}`;
};

const applyStageTemplate = template => {
  state.wahaMessage = template.content || template.title || '';
};

const MEDIA_TYPES = [
  'image/jpeg',
  'image/png',
  'image/webp',
  'image/gif',
  'video/mp4',
  'video/3gpp',
  'video/quicktime',
  'application/pdf',
];

const uploadMedia = async file => {
  if (!MEDIA_TYPES.includes(file.type)) {
    useAlert('Tipo não suportado: use imagem, vídeo (MP4) ou PDF.');
    return null;
  }
  if (file.size > 16 * 1024 * 1024) {
    useAlert('Arquivo maior que 16MB.');
    return null;
  }
  const { fileUrl, blobId } = await uploadFile(file);
  return { blobId, fileName: file.name, fileUrl, contentType: file.type };
};

const handleMediaUpload = async event => {
  const file = event.target.files?.[0];
  if (!file) return;
  state.mediaUploading = true;
  try {
    const media = await uploadMedia(file);
    if (media) state.attachedMedia = media;
  } catch (error) {
    useAlert('Não foi possível subir o arquivo. Tente de novo.');
  } finally {
    state.mediaUploading = false;
  }
};

const handleVariantMedia = async (idx, event) => {
  const file = event.target.files?.[0];
  if (!file) return;
  try {
    const media = await uploadMedia(file);
    if (media) state.messageVariants[idx].media = media;
  } catch (error) {
    useAlert('Não foi possível subir o arquivo. Tente de novo.');
  }
};

const isDirty = () =>
  !!(
    state.title?.trim() ||
    state.wahaMessage?.trim() ||
    state.templateId ||
    state.attachedMedia ||
    state.kanbanItemIds.length ||
    state.selectedAudience.length ||
    csvContacts.value.length
  );

const reset = () => {
  Object.assign(state, createInitialState());
  state.channelType = props.initialChannel;
  csvContacts.value = [];
  preview.value = null;
  currentIndex.value = 0;
  furthestIndex.value = 0;
  resolvedForFamily.value = null;
  skipChannelStep.value = false;
  applyChannelEntry();
};

defineExpose({ isDirty, reset, state, steps, currentIndex, skipChannelStep });
</script>

<template>
  <div class="flex flex-col gap-4">
    <CampaignStepBar
      :steps="steps"
      :current-index="currentIndex"
      :furthest-index="furthestIndex"
      @go="jumpToStep"
    />

    <!-- Os blocos ficam montados (v-show): Voltar acha tudo como estava e o
         parser do modelo aprovado não some do meio do caminho. -->
    <div class="flex flex-col flex-1 min-w-0 gap-3">
      <FormBlock
        v-show="showStep('channel')"
        :number="numeroDoBloco('channel')"
        title="Por onde sai"
        :pending="pendingBlock === 1"
      >
        <ChannelBlock
          :state="state"
          :inbox-options="inboxOptions"
          :availability="availability"
          :allowed-channels="channelFamily"
          @change-channel="changeChannel"
        />
      </FormBlock>

      <FormBlock
        v-show="showStep('audience')"
        :number="numeroDoBloco('audience')"
        title="Para quem"
        :pending="pendingBlock === 2"
      >
        <AudienceBlock
          :state="state"
          :label-options="labelOptions"
          :funnel-options="funnelOptions"
          :stage-options="stageOptions"
          :agent-options="agentOptions"
          :contact-attribute-options="contactAttributeOptions"
          :contact-attr-value-options="contactAttrValueOptions"
          :csv-chip-text="csvChipText"
          :csv-count="csvContacts.length"
          :kanban-funnel-name="kanbanFunnelName"
          :preview="preview"
          :preview-loading="previewLoading"
          :preview-error="previewError"
          :suppression-loading="suppressionLoading"
          :suppression-error="suppressionError"
          :suppression-result="suppressionResult"
          @change-type="changeAudienceType"
          @csv-upload="handleCsvUpload"
          @csv-remove="removeCsv"
          @csv-template="downloadCsvTemplate"
          @retry-preview="loadPreview"
          @suppression-upload="handleSuppressionUpload"
          @retry-suppression="processSuppressionFile(suppressionRetryFile)"
          @clear-kanban-items="clearKanbanItems"
        />
      </FormBlock>

      <FormBlock
        v-show="showStep('message')"
        :number="numeroDoBloco('message')"
        title="O que diz"
        :pending="pendingBlock === 3"
      >
        <MessageBlock
          ref="messageBlockRef"
          :state="state"
          :is-free-text="isFreeText"
          :is-waha="isWaha"
          :template-options="templateOptions"
          :selected-template="selectedTemplate"
          :stage-templates="stageTemplates"
          :variable-options="VARIABLES"
          @insert-variable="insertVariable"
          @add-variant="state.messageVariants.push({ text: '', media: null })"
          @remove-variant="state.messageVariants.splice($event, 1)"
          @media-upload="handleMediaUpload"
          @remove-media="state.attachedMedia = null"
          @variant-media="handleVariantMedia"
          @remove-variant-media="state.messageVariants[$event].media = null"
          @apply-stage-template="applyStageTemplate"
          @add-template-variant="
            state.templateVariants.push({ templateId: null })
          "
          @remove-template-variant="state.templateVariants.splice($event, 1)"
        />
      </FormBlock>

      <FormBlock
        v-show="showStep('schedule')"
        :number="numeroDoBloco('schedule')"
        title="Quando e em que ritmo"
        :pending="pendingBlock === 4"
      >
        <ScheduleBlock
          ref="scheduleBlockRef"
          :state="state"
          :channel-mode="channelMode"
          :is-free-text="isFreeText"
          :recipient-count="receiving"
          :duration="duration"
          :agent-options="agentOptions"
          :team-options="teamOptions"
          :ai-assistant-options="aiAssistantOptions"
          :funnel-options="funnelOptions"
          :kanban-dest-stage-options="kanbanDestStageOptions"
          :whatsapp-inbox-options="whatsappInboxOptions"
          :ka-template-options="kaTemplateOptions"
          :ka-selected-template="kaSelectedTemplate"
          :ka-target-is-free-text="kaTargetIsFreeText"
          :min-date-time="minDateTime"
        />
      </FormBlock>
    </div>

    <!-- Última tela: conferir antes de enviar. -->
    <div v-show="isReviewStep" class="w-full">
      <CampaignSummary
        :state="state"
        :channel-label="CHANNEL_LABELS[state.channelType]"
        :inbox-name="selectedInbox?.name || ''"
        :receiving="receiving"
        :excluded="excluded"
        :when-label="whenLabel"
        :finish-label="finishLabel"
        :preview-message="previewMessage"
        :preview-recipient-name="previewRecipientName"
        :preview-loading="previewLoading"
        :missing-label="missingLabel"
        :submitting="submitting"
        :can-save-draft="true"
        @submit="handleSubmit"
        @save-draft="handleSaveDraft"
      />
    </div>

    <!-- Continuar e Voltar. Na tela de conferir quem envia é o resumo, então
         aqui fica só o Voltar. -->
    <div class="flex items-center justify-between gap-3">
      <Button
        v-if="!isFirstStep"
        label="Voltar"
        icon="i-ph-arrow-left"
        variant="faded"
        color="slate"
        size="sm"
        data-testid="campaign-step-back"
        @click="goBack"
      />
      <span v-else />
      <Button
        v-if="!isReviewStep"
        :label="continueLabel"
        :disabled="!canContinue"
        color="blue"
        size="sm"
        data-testid="campaign-step-next"
        @click="goNext"
      />
    </div>
  </div>
</template>
